import axios, {
  type AxiosAdapter,
  AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
  getAdapter,
} from 'axios';
import type { LDContext } from 'launchdarkly-react-client-sdk';

import { version } from '../../package.json';
import { getUserLanguage } from '../i18n/helpers';
import { getSecondsSinceAppMount, getSecondsSinceSessionStart } from '../utils/appMount';
import { CancellationError } from '../utils/errors';
import type { MaybePromise } from '../utils/types';

import { getBrowserSessionId } from './analytics/getBrowserSessionId';
import { firebaseAuth } from './firebase';
import { createLDContext, identifyUser } from './launchDarkly';
import type { ApiResponse } from './types';

const IDENTIFY_USER_ROUTES_SET = new Set(['/v2/user/profile', '/v1/user/profile', '/v1/user/login']);

function makeMockAdapter(mocksPath: string, standardAdapter: AxiosAdapter): AxiosAdapter {
  return async function adapter(config) {
    const { default: mocks } = await import(/* @vite-ignore */ mocksPath);

    const forEndpoint = mocks[config.url || ''];
    const forMethod = forEndpoint?.[config.method?.toUpperCase() || 'GET'];

    if (!forMethod) {
      if (typeof standardAdapter !== 'function') {
        throw new Error('No axios adapter found');
      }

      return standardAdapter(config);
    }

    if (typeof config.validateStatus === 'function' && config.validateStatus(forMethod.status)) {
      return {
        ...forMethod,
        config,
      };
    }

    throw new AxiosError(
      `[MOCK] Request failed with status code ${forMethod.status}`,
      [AxiosError.ERR_BAD_REQUEST, AxiosError.ERR_BAD_RESPONSE][Math.floor(forMethod.status / 100) - 4],
      config,
    );
  };
}

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    Accept: 'application/json',
  },
  withCredentials: true,
});

const standardAdapter = getAdapter(axiosInstance.defaults.adapter);

axiosInstance.defaults.adapter = import.meta.env.VITE_API_MOCKS_PATH
  ? makeMockAdapter(import.meta.env.VITE_API_MOCKS_PATH, standardAdapter)
  : undefined;

/**
 * Public API client — no Authorization header, no custom x-* headers.
 * Use for endpoints that don't require auth (landing, banners, config).
 * Benefits: no CORS preflight, responses are edge-cacheable.
 *
 * Note: withCredentials is omitted intentionally — public endpoints
 * don't need cookies. If a public endpoint starts needing cookie-based
 * geo detection, add withCredentials: true here.
 */
const publicAxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    Accept: 'application/json',
  },
});

publicAxiosInstance.interceptors.request.use((config) => {
  if (config.url && !config.url.startsWith('/v2')) {
    config.url = `/v1/${config.url}`;
  }
  return config;
});

publicAxiosInstance.interceptors.response.use((response) => {
  return response.data;
});

async function getAccessToken() {
  return firebaseAuth.currentUser?.getIdToken();
}

axiosInstance.interceptors.request.use(async (config) => {
  const token = await getAccessToken();

  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }

  config.headers.set('x-platform', 'WEB');
  config.headers.set('x-app-version', version);
  config.headers.set('x-browser-session-id', getBrowserSessionId());

  if (config.url && !config.url.startsWith('/v2')) {
    config.url = `/v1/${config.url}`;
  }

  config.headers.set('x-session-duration-secs', getSecondsSinceSessionStart().toString());
  config.headers.set('x-app-mount-duration-secs', getSecondsSinceAppMount().toString());
  config.headers.set('x-app-selected-lang', getUserLanguage());

  return config;
});

axiosInstance.interceptors.response.use((response) => {
  if (IDENTIFY_USER_ROUTES_SET.has(response.config.url || '')) {
    if (response.data?.data) {
      let identifyData: LDContext | null = null;

      if (response.config.url === '/v1/user/login' && response.data.data?.user) {
        identifyData = createLDContext(response.data.data.user);
      } else if (
        response.config.url &&
        ['/v1/user/profile', '/v2/user/profile'].includes(response.config.url) &&
        response.data.data.data
      ) {
        identifyData = createLDContext(response.data.data.data);
      }

      if (identifyData) {
        void identifyUser(identifyData);
      }
    }
  }

  return response.data;
});

export const addApiCallInterceptor = (
  interceptor: (config: InternalAxiosRequestConfig) => MaybePromise<InternalAxiosRequestConfig>,
) => {
  const id = axiosInstance.interceptors.request.use(interceptor);

  return () => axiosInstance.interceptors.request.eject(id);
};

async function executeGet<T>(
  instance: typeof axiosInstance,
  url: string,
  params?: Record<string, unknown>,
  abortSignal?: AbortSignal,
): Promise<T> {
  try {
    return await instance.get<T, T>(url, {
      params,
      signal: abortSignal,
      transitional: {
        clarifyTimeoutError: true,
      },
    });
  } catch (error) {
    if (
      error instanceof AxiosError &&
      (error.code === AxiosError.ETIMEDOUT || error.code === AxiosError.ERR_CANCELED)
    ) {
      throw new CancellationError(`Axios request to ${url}`);
    }

    throw error;
  }
}

export async function getRequest<T = ApiResponse<never>>(
  url: string,
  params?: Record<string, unknown>,
  abortSignal?: AbortSignal,
): Promise<T> {
  return executeGet<T>(axiosInstance, url, params, abortSignal);
}

export async function postRequest<T = ApiResponse<never>>(
  url: string,
  body?: object | FormData,
  config?: AxiosRequestConfig,
): Promise<T> {
  return axiosInstance.post<T, T>(url, body, config);
}

export async function deleteRequest<T = ApiResponse<never>>(url: string, body?: object | FormData): Promise<T> {
  return axiosInstance.delete<T, T>(url, { data: body });
}

export async function putRequest<T = ApiResponse<never>>(url: string, body?: object | FormData): Promise<T> {
  return axiosInstance.put<T, T>(url, body);
}

export async function patchRequest<T = ApiResponse<never>>(url: string, body?: object | FormData): Promise<T> {
  return axiosInstance.patch<T, T>(url, body);
}

export async function publicGetRequest<T = ApiResponse<never>>(
  url: string,
  params?: Record<string, unknown>,
  abortSignal?: AbortSignal,
): Promise<T> {
  return executeGet<T>(publicAxiosInstance, url, params, abortSignal);
}
